import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, Ghost, Mail, Phone, Send, Loader2, Truck } from "lucide-react";
import { toast } from "sonner";
import { api, whatsappLink, waNumber, formatApiErrorDetail } from "../lib/api";
import { usePageTitle } from "../lib/usePageTitle";

const SNAP_URL = "https://snapchat.com/t/6VjWbSHH";
const EMAIL = "contact@stuff.cbd";
const WA_TEXT = "Bonjour STUFF France, j'ai une question :";

function fmtPhone(n) {
  const d = (n || "").replace(/[^0-9]/g, "");
  return d.length === 10 ? d.replace(/(\d{2})(?=\d)/g, "$1 ") : n;
}

export default function Contact() {
  usePageTitle("Contact — STUFF CBD France | WhatsApp, Snapchat, formulaire", "Contacte la boutique STUFF France par WhatsApp, Snapchat ou via le formulaire : question sur une commande, un produit, la livraison 48h ou pour devenir vendeur.");
  const [national, setNational] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => { api.get("/vendors/national").then((r) => setNational(r.data)).catch(() => setNational({})); }, []);
  const wa = national?.whatsapp || national?.phone || "";
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2 || form.message.trim().length < 5) { toast.error("Indique ton nom et un message (5 caractères minimum)"); return; }
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Message envoyé ! On te répond vite.", { style: { background: "#121212", color: "#9dff00", border: "1px solid #9dff00" } });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSending(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10" data-testid="contact-page">
      <p className="text-neon-magenta font-graffiti text-xl">On te répond en direct</p>
      <h1 className="font-anton text-4xl sm:text-5xl uppercase text-white mb-2">Contact <span className="neon-text-green">STUFF France</span></h1>
      <p className="text-zinc-400 text-sm sm:text-base max-w-2xl mb-10">Une question sur une commande, un produit, la livraison 48h ou envie de devenir vendeur dans ta ville ? Le plus rapide : WhatsApp. Sinon, laisse-nous un message.</p>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 space-y-3">
          <a data-testid="contact-whatsapp" href={wa ? whatsappLink(wa, WA_TEXT) : undefined} target="_blank" rel="noopener noreferrer"
            className={`block border border-neon-green bg-neon-green/5 p-5 hover:bg-neon-green/10 transition-colors ${wa ? "" : "opacity-50 pointer-events-none"}`}>
            <span className="flex items-center gap-2 text-neon-green font-anton uppercase text-lg"><MessageCircle className="h-5 w-5" /> WhatsApp boutique France</span>
            <span data-testid="contact-whatsapp-number" className="block text-white font-anton text-2xl mt-1">{wa ? fmtPhone(wa) : "…"}</span>
            <span className="block text-zinc-500 text-xs mt-1">Réponse rapide 7j/7 · commandes, suivi, conseils produits</span>
          </a>
          {wa && (
            <a data-testid="contact-call" href={`tel:+${waNumber(wa)}`} className="flex items-center gap-2 border border-zinc-700 p-4 text-zinc-200 hover:border-neon-green hover:text-neon-green transition-colors">
              <Phone className="h-4 w-4" /> Appeler la boutique
            </a>
          )}
          <a data-testid="contact-snapchat" href={SNAP_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 border border-[#FFFC00]/60 p-4 text-[#FFFC00] hover:bg-[#FFFC00]/10 transition-colors">
            <Ghost className="h-4 w-4" /> Snapchat officiel
          </a>
          <a data-testid="contact-email" href={`mailto:${EMAIL}`} className="flex items-center gap-2 border border-zinc-700 p-4 text-zinc-200 hover:border-neon-magenta hover:text-neon-magenta transition-colors">
            <Mail className="h-4 w-4" /> {EMAIL}
          </a>
          <Link data-testid="contact-national-shop" to="/boutique/national" className="flex items-center gap-2 border border-zinc-800 bg-ink-surface p-4 text-zinc-400 hover:text-white transition-colors text-sm">
            <Truck className="h-4 w-4 text-neon-green" /> Commander en livraison 48h partout en France →
          </Link>
        </div>

        <div className="lg:col-span-3 border border-zinc-800 bg-ink-surface p-5 sm:p-6">
          {sent ? (
            <div data-testid="contact-success" className="text-center py-10">
              <p className="font-anton text-2xl uppercase text-neon-green">Message envoyé ✅</p>
              <p className="text-zinc-400 text-sm mt-2">Merci {form.name} ! On revient vers toi par {form.phone ? "WhatsApp" : "email"} très vite.</p>
              <button data-testid="contact-again" onClick={() => { setSent(false); setForm({ name: "", email: "", phone: "", message: "" }); }} className="mt-6 text-xs uppercase font-bold text-zinc-400 hover:text-neon-green">Envoyer un autre message</button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3" data-testid="contact-form">
              <h2 className="font-anton uppercase text-white text-lg">Laisse-nous un message</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input data-testid="contact-name" value={form.name} onChange={upd("name")} placeholder="Ton nom *" maxLength={80} required className="bg-ink border border-zinc-700 px-4 py-3 text-white placeholder-zinc-600 focus:border-neon-green outline-none" />
                <input data-testid="contact-phone" value={form.phone} onChange={upd("phone")} placeholder="Téléphone / WhatsApp" maxLength={25} inputMode="tel" className="bg-ink border border-zinc-700 px-4 py-3 text-white placeholder-zinc-600 focus:border-neon-green outline-none" />
              </div>
              <input data-testid="contact-email-input" type="email" value={form.email} onChange={upd("email")} placeholder="Email" maxLength={120} className="w-full bg-ink border border-zinc-700 px-4 py-3 text-white placeholder-zinc-600 focus:border-neon-green outline-none" />
              <textarea data-testid="contact-message" value={form.message} onChange={upd("message")} placeholder="Ton message * (commande, produit, livraison, devenir vendeur…)" rows={5} maxLength={2000} required className="w-full bg-ink border border-zinc-700 px-4 py-3 text-white placeholder-zinc-600 focus:border-neon-green outline-none resize-y" />
              <button type="submit" data-testid="contact-submit" disabled={sending} className="w-full flex items-center justify-center gap-2 bg-neon-green text-black font-anton uppercase text-base py-3.5 glow-green hover:bg-neon-greenDark transition-colors disabled:opacity-50">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Envoyer
              </button>
              <p className="text-zinc-600 text-[11px]">Tes infos servent uniquement à te répondre. Aucune newsletter. <Link to="/confidentialite" className="underline hover:text-zinc-400">Confidentialité</Link></p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
